<template>
  <v-form ref="formRef" class="boiler-form method-form">
    <!-- Kesseldaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-water-boiler</v-icon>
        <span>Kesseldaten</span>
      </div>

      <v-text-field
        v-model.number="form.constructionYear"
        type="number"
        :error-messages="validationErrors?.construction_year"
        label="Baujahr"
        :hint="hint.construction_year"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.construction_year" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.construction_year }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.construction_year"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('construction_year')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-autocomplete
        v-model="form.constructionType"
        :error-messages="validationErrors?.construction_type"
        label="Bauart"
        :items="constructionTypes"
        item-title="name"
        item-value="id"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.construction_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.construction_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.construction_type"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('construction_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-text-field
        v-model.number="form.nominalPower"
        type="number"
        :error-messages="validationErrors?.nominal_power"
        label="Nennleistung"
        suffix="kW"
        :hint="hint.nominal_power"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.nominal_power" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.nominal_power }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.nominal_power"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('nominal_power')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-autocomplete
        v-model="form.fuel"
        :error-messages="validationErrors?.fuel"
        label="Brennstoff"
        :items="fuelTypes"
        :disabled="!form.constructionType"
        item-title="name"
        item-value="id"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.fuel" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.fuel }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.fuel"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('fuel')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Temperaturen -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-thermometer-lines</v-icon>
        <span>Temperaturen</span>
      </div>

      <v-autocomplete
        v-model="form.tetaVl"
        :error-messages="validationErrors?.teta_vl"
        label="Vorlauftemperatur"
        :items="tetaVlItems"
        suffix="°C"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.teta_vl" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.teta_vl }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.teta_vl"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('teta_vl')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.tetaRl"
        :error-messages="validationErrors?.teta_rl"
        label="Rücklauftemperatur"
        :items="tetaRlItems"
        suffix="°C"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.teta_rl" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.teta_rl }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.teta_rl"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('teta_rl')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-checkbox
        v-model="form.temperatureSlip"
        :error-messages="validationErrors?.temperature_slip"
        label="Temperaturgleitung"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.temperature_slip" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.temperature_slip }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.temperature_slip"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('temperature_slip')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>
    </div>

    <!-- Betrieb -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betrieb</span>
      </div>

      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Jährliche Betriebsstunden"
        suffix="h"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Energiebedarf -->
    <div v-if="reference" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-fire-circle</v-icon>
        <span>Energiebedarf</span>
      </div>

      <v-text-field
        v-model.number="form.energyConsumption"
        type="number"
        :error-messages="validationErrors?.energy_consumption"
        label="Energiebedarf"
        suffix="kWh/a"
        :hint="hint.energy_consumption"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.energy_consumption" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.energy_consumption }}</span>
          </v-tooltip>
        </template>
      </v-text-field>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import api from '@/plugins/axios'

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference: { type: Boolean, default: false },
  planned:   { type: Boolean, default: false },
  otherData: { type: Object, default: () => ({}) },
  sharedData:{ type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) }, // passed to display field errors
  methodValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const formRef = ref(null)
function validateForm() { return formRef.value?.validate?.() }


// Boiler fields map
const fields = {
  constructionYear:  'construction_year',
  constructionType:  'construction_type',
  nominalPower:      'nominal_power',
  fuel:              'fuel',
  tetaVl:            'teta_vl',
  tetaRl:            'teta_rl',
  temperatureSlip:   'temperature_slip',
  uptime:            'uptime',
  energyConsumption: 'energy_consumption',
}


defineExpose({
  validateForm,
  fields: Object.values(fields),
})


// Optional defaults (e.g., checkbox false)
const defaults = {
  temperatureSlip: false,
}

const {
  form,              // <-- bind your inputs to form.frontendKey
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults,
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => false), // Boiler is NOT a shared form
  otherData: computed(() => props.otherData),
  sharedData:computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

// Dynamic selects
const tetaVlItems = [28,30,35,40,45,50,55,60,70,80,90]
const tetaRlItems = [...tetaVlItems]
const constructionTypes = ref([])
const fuelTypes = ref([])

async function fetchConstructionTypes() {
  try {
    const { data } = await api.get('/methods/boiler/types/', {
      params: { planned: props.planned || null, reference: props.reference || null },
    })
    constructionTypes.value = data
  } catch (err){
    console.error('Error fetching construction types:', err)
  }
}

async function fetchFuelTypes() {
  if (!form.constructionType) return
  try {
    const { data } = await api.get('/methods/boiler/fuel/', {
      params: { construction: form.constructionType },
    })
    fuelTypes.value = data
    if (fuelTypes.value.length === 1) form.fuel = fuelTypes.value[0].id
  } catch (err){
    console.error('Error fetching fuel types:', err)
  }
}

watch(() => form.constructionType, fetchFuelTypes, { immediate: true })

onMounted(() => {
  fetchConstructionTypes()
  fetchHints()
})
</script>


<style scoped lang="scss">
.boiler-form {
  width: 100%;
}

/* Field styling */
.boiler-form :deep(.v-field) {
  border-radius: 18px;
}

.boiler-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkboxes */
.boiler-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested/dependent fields */
.boiler-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint/copy icons */
.boiler-form :deep(.v-input__append),
.boiler-form :deep(.v-field__append-inner) {
  align-items: center;
}

.boiler-form :deep(.v-input__append .v-icon),
.boiler-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.boiler-form :deep(.v-input__append .v-btn),
.boiler-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.boiler-form :deep(.v-input__append .v-btn:hover),
.boiler-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled fields */
.boiler-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .boiler-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>